import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../config';

const CreditContext = createContext();

export const useCredits = () => {
  const context = useContext(CreditContext);
  if (!context) {
    throw new Error('useCredits must be used within a CreditProvider');
  }
  return context;
};

export const CreditProvider = ({ children }) => {
  const [credits, setCredits] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const fetchCredits = useCallback(async (retryCount = 0) => {
    try {
      const response = await fetch(getApiUrl('/user/credits'), {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setCredits(data.credits);
      }
    } catch (error) {
      console.error('Failed to fetch credits:', error);
      
      // Retry up to 3 times with exponential backoff
      if (retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        console.log(`Retrying credits fetch in ${delay}ms (attempt ${retryCount + 1}/3)`);
        setTimeout(() => fetchCredits(retryCount + 1), delay);
      }
    }
  }, []);

  const triggerCreditAnimation = useCallback(() => {
    setIsAnimating(true);
    // Reset animation state after animation completes
    setTimeout(() => {
      setIsAnimating(false);
    }, 600);
  }, []);

  const updateCreditsFromResponse = useCallback((response) => {
    if (response.credits_used && response.remaining_credits !== undefined) {
      setCredits(response.remaining_credits);
      triggerCreditAnimation();
    }
  }, [triggerCreditAnimation]);

  // Function to refresh credits after login
  const refreshCreditsAfterLogin = useCallback(() => {
    // Small delay to ensure session is established
    setTimeout(() => {
      fetchCredits();
    }, 100);
  }, [fetchCredits]);

  // Fetch credits on mount
  useEffect(() => {
    // Add a small delay to prevent race conditions with other auth checks
    setTimeout(() => fetchCredits(), 1500);
  }, [fetchCredits]);

  const value = {
    credits,
    isAnimating,
    fetchCredits,
    triggerCreditAnimation,
    updateCreditsFromResponse,
    refreshCreditsAfterLogin,
  };

  return (
    <CreditContext.Provider value={value}>
      {children}
    </CreditContext.Provider>
  );
}; 